import {
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  SimpleGrid,
  VStack,
  chakra,
  useBreakpointValue,
} from "@chakra-ui/react";
import { ReactElement, useCallback, useEffect, useMemo, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useClickAnyWhere, useEventListener } from "usehooks-ts";
import { isColumnPair, useDataSource } from "../DataSource";
import { SIDEBAR_WIDTH } from "../config";
import { useEffectOnce } from "../hooks/useEffectOnce";
import ArticleMap from "./ArticleMap";
import ArticleMarkdown from "./ArticleMarkdown";
import ErrorView from "./ErrorView";
import LoadingSpinner from "./LoadingSpinner";
import DataHeatmap, { DataHeatmapConfig } from "./DataHeatmap";
import Sidebar from "./Sidebar";
import { useResizeObserver } from "../hooks/useResizeObserver";
import { replaceItem } from "../core/utils";
import { AiFillDelete } from "react-icons/ai";
import Heading from "./Heading";
import Markdown from "./Markdown";
import HeatmapDownloadButton from "./DataHeatmap/HeatmapDownloadButton";

interface EmbedMessage {
  height?: number;
  ready?: true;
}

export default function Embed(): ReactElement {
  const {
    focused,
    config,
    filters,
    setFilters,
    setMapHighlight,
    articles: filteredArticles,
    allArticles,
  } = useDataSource();

  const [parent100vh, setParent100vh] = useState<number | null>(null);
  const [contentRef, contentRefRect] = useResizeObserver<HTMLDivElement>();

  const columns = useMemo(() => filters.map(({ column }) => column), [filters]);
  const [heatmapConfigs, setHeatmapConfigs] = useState<
    Array<DataHeatmapConfig>
  >(() => [{ xAxis: null, yAxis: null }]);

  const [contentGridRef, contentGridRect] = useResizeObserver<HTMLDivElement>();

  const [error, setError] = useState<Error | null>(null);
  if (error != null) {
    throw error;
  }

  const isTop = window.top === window;

  const noResponseTimeout = useMemo(
    () =>
      !isTop
        ? setTimeout(() => {
            setError(
              new Error(
                "No response from the top page. Make sure you have added the correct script tag"
              )
            );
          }, 5000)
        : null,
    [isTop]
  );

  useEventListener("message", (evt) => {
    if (Number.isSafeInteger(evt.data?.mapEmbed?.["100vh"])) {
      if (noResponseTimeout != null) {
        clearTimeout(noResponseTimeout);
      }
      setParent100vh(evt.data.mapEmbed["100vh"]);
    }
  });

  useEffectOnce(() => messageTop({ ready: true }));

  useEffect(() => {
    if (
      !isTop &&
      parent100vh != null &&
      contentRefRect != null &&
      contentRefRect.height > 0
    ) {
      messageTop({ height: Math.ceil(contentRefRect.height) });
    }
  }, [contentRefRect, isTop, parent100vh]);

  useEffect(() => {
    if (focused == null && parent100vh != null) {
      messageTop({ height: Math.round(parent100vh * 0.7) });
    }
  }, [focused, parent100vh]);

  const contentDoubleColumn = useBreakpointValue([false, false, false, true]);
  const sidebarDoubleColumn = useBreakpointValue([false, true]);

  const handleMapHighlightChange = useCallback(
    ({ target }: { target: EventTarget | null }) => {
      const el = target as HTMLElement | null;
      const columnPairs = [
        {
          column: el?.getAttribute("data-x-axis"),
          value: el?.getAttribute("data-x-option"),
        },
        {
          column: el?.getAttribute("data-y-axis"),
          value: el?.getAttribute("data-y-option"),
        },
      ].filter(isColumnPair);
      if (columnPairs.length === 2) {
        setMapHighlight(columnPairs);
      } else if (el?.closest("[data-map-highlight-click-ignore]") == null) {
        setMapHighlight(null);
      }
    },
    [setMapHighlight]
  );

  useClickAnyWhere(handleMapHighlightChange);

  return (
    <chakra.main ref={contentRef}>
      {(config.title != null || config.blurb != null) && (
        <chakra.header ms={sidebarDoubleColumn ? SIDEBAR_WIDTH : undefined}>
          <VStack w={contentDoubleColumn ? "70%" : "100%"}>
            {config.title != null && (
              <Heading level={3} px="3" pt="3">
                {config.title}
              </Heading>
            )}
            {config.blurb != null && <Markdown template={config.blurb} />}
          </VStack>
        </chakra.header>
      )}
      <Flex
        flexDirection={sidebarDoubleColumn ? "row" : "column"}
        alignItems="flex-start"
        w="100%"
      >
        <Sidebar
          filters={filters}
          onChange={setFilters}
          minW={SIDEBAR_WIDTH}
          maxW={SIDEBAR_WIDTH}
          overflowY="auto"
          maxH="90rem"
          borderEndWidth="1px"
        />
        <VStack
          ref={contentGridRef}
          w="100%"
          gap="0"
          wrap="wrap"
          align="stretch"
        >
          <SimpleGrid
            gridTemplateColumns={contentDoubleColumn ? "70% 30%" : "100%"}
            maxW={
              contentGridRect?.width != null
                ? `${contentGridRect.width}px`
                : "100%"
            }
          >
            <VStack
              spacing="0"
              ms="-1px"
              borderStartWidth="1px"
              align="stretch"
            >
              <ErrorBoundary FallbackComponent={ErrorView}>
                <Box data-map-highlight-click-ignore>
                  {contentGridRect == null ? (
                    <LoadingSpinner text="Loading content" />
                  ) : isTop ? (
                    <ArticleMap height="70vh" />
                  ) : parent100vh != null ? (
                    <ArticleMap height={`${parent100vh * 0.7}px`} />
                  ) : (
                    <LoadingSpinner text="Waiting for top page to respond" />
                  )}
                </Box>
              </ErrorBoundary>

              {!contentDoubleColumn && (
                <ArticleMarkdown className="article-preview" />
              )}

              {filters.length > 2 && (
                <>
                  {heatmapConfigs.map((heatmapConfig, i) => (
                    <Box
                      key={i}
                      borderEndWidth={contentDoubleColumn ? "1px" : undefined}
                      borderTopWidth={i > 0 ? "1px" : undefined}
                    >
                      <HStack m="2" justifyContent="flex-end">
                        {heatmapConfig.xAxis != null &&
                          heatmapConfig.yAxis != null && (
                            <>
                              <HeatmapDownloadButton
                                articles={allArticles}
                                xAxis={heatmapConfig.xAxis}
                                yAxis={heatmapConfig.yAxis}
                              >
                                Download All Data
                              </HeatmapDownloadButton>
                              <HeatmapDownloadButton
                                articles={filteredArticles}
                                xAxis={heatmapConfig.xAxis}
                                yAxis={heatmapConfig.yAxis}
                              >
                                Download Filtered Data
                              </HeatmapDownloadButton>
                            </>
                          )}
                        {heatmapConfigs.length > 1 && (
                          <Button
                            size="sm"
                            onClick={() =>
                              setHeatmapConfigs(replaceItem(heatmapConfigs, i))
                            }
                          >
                            <Icon as={AiFillDelete} w="1.25rem" h="1.25rem" />
                          </Button>
                        )}
                      </HStack>
                      <Flex
                        pt="8"
                        w="100%"
                        justify={
                          heatmapConfig.xAxis == null ||
                          heatmapConfig.yAxis == null
                            ? "center"
                            : undefined
                        }
                        overflowX="auto"
                      >
                        <DataHeatmap
                          {...heatmapConfig}
                          columns={columns}
                          onXAxisChange={(xAxis) =>
                            setHeatmapConfigs(
                              replaceItem(heatmapConfigs, i, {
                                ...heatmapConfig,
                                xAxis,
                              })
                            )
                          }
                          onYAxisChange={(yAxis) =>
                            setHeatmapConfigs(
                              replaceItem(heatmapConfigs, i, {
                                ...heatmapConfig,
                                yAxis,
                              })
                            )
                          }
                          px="8"
                          w="fit-content"
                          className="data-heatmap"
                        />
                      </Flex>
                    </Box>
                  ))}
                  <Flex
                    justify="flex-end"
                    borderEndWidth={contentDoubleColumn ? "1px" : undefined}
                  >
                    <Button
                      my="2"
                      me="2"
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        setHeatmapConfigs([
                          ...heatmapConfigs,
                          { xAxis: null, yAxis: null },
                        ])
                      }
                    >
                      Create another heatmap
                    </Button>
                  </Flex>
                </>
              )}
            </VStack>
            {contentDoubleColumn && (
              <ArticleMarkdown
                ms="-1px"
                borderStartWidth="1px"
                className="article-preview"
              />
            )}
          </SimpleGrid>
        </VStack>
      </Flex>
    </chakra.main>
  );
}

function messageTop(msg: EmbedMessage) {
  window.parent.postMessage({ mapEmbed: msg }, "*");
}
